import { ActionItemCard } from "@/components/submissions/ActionItemCard";
import { buttonClass } from "@/components/buttonStyles";
import type { BrokerQuote } from "@/lib/submissions/brokerSubmissions";
import { actionItemsOf, forBroker } from "@/lib/submissions/actionItems";
import { useActionItems } from "@/lib/submissions/useActionItems";

/**
 * The quote popup's Actions tab (BRK-S465): what Signal works for the broker
 * on this quote (PMS-S872), what Signal waits on first (BRK-S464), then most
 * recently updated. PMS lists the submission's, so the quote's own are kept.
 * It reads them again each time the quotes are, so a review just asked for
 * shows here, and after a reply. A PMS before PMS-S872 has none to show,
 * never an error.
 */
export function QuoteActionItems({
  submissionId,
  quote,
}: {
  submissionId: string;
  /** A new object each time the quotes are read again. */
  quote: BrokerQuote;
}) {
  const { read, reload, retry } = useActionItems(submissionId, true, quote);

  if (read.kind === "loading")
    return (
      <p role="status" className="text-sm text-brk-dim">
        Getting this quote’s actions…
      </p>
    );
  if (read.kind === "failed")
    return (
      <div role="alert" className="flex flex-col gap-1 text-sm">
        <p className="text-brk-danger">We couldn’t get this quote’s actions.</p>
        <button
          type="button"
          onClick={retry}
          className={`${buttonClass({ variant: "secondary", size: "sm" })} self-start`}
        >
          Retry
        </button>
      </div>
    );
  const items = forBroker(actionItemsOf(read.items, quote.id));
  if (items.length === 0)
    return (
      <p className="text-sm text-brk-dim">No actions on this quote yet.</p>
    );
  return (
    <div className="flex flex-col gap-3 text-sm">
      {items.map((item) => (
        <ActionItemCard
          key={item.id}
          item={item}
          submissionId={submissionId}
          onChanged={reload}
        />
      ))}
    </div>
  );
}
